import { useEffect, useState, useCallback } from 'react';
import { Smartphone, X, Download } from 'lucide-react';

const DISMISS_KEY = 'digi_pwa_install_dismissed';
const DISMISS_DAYS = 7; // re-prompt after a week
const isStandalone = () =>
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true; // iOS

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

const wasRecentlyDismissed = () => {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (!ts) return false;
    return (Date.now() - ts) < DISMISS_DAYS * 24 * 60 * 60 * 1000;
};

const InstallPrompt = () => {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [show, setShow] = useState(false);
    const [showIOSHint, setShowIOSHint] = useState(false);

    const dismiss = useCallback(() => {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
        setShow(false);
        setShowIOSHint(false);
    }, []);

    useEffect(() => {
        if (isStandalone() || wasRecentlyDismissed()) return;

        // Android / Chrome / Edge desktop
        const onBefore = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setShow(true);
        };
        window.addEventListener('beforeinstallprompt', onBefore);

        // iOS Safari (no beforeinstallprompt event) — show hint banner once after 4s
        if (isIOS()) {
            const t = setTimeout(() => setShowIOSHint(true), 4000);
            return () => clearTimeout(t);
        }

        return () => window.removeEventListener('beforeinstallprompt', onBefore);
    }, []);

    const handleInstall = async () => {
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        try {
            const choice = await deferredPrompt.userChoice;
            if (choice.outcome === 'accepted') {
                setShow(false);
            } else {
                dismiss();
            }
        } catch (e) {
            dismiss();
        }
        setDeferredPrompt(null);
    };

    if (!show && !showIOSHint) return null;

    return (
        <div
            className="fixed bottom-20 sm:bottom-4 left-1/2 -translate-x-1/2 z-[60] w-[92%] max-w-md"
            role="dialog"
            aria-label="Install app prompt"
            data-testid="pwa-install-prompt"
        >
            <div className="bg-gradient-to-br from-[#2563eb] to-[#1e3a8a] text-white rounded-2xl shadow-2xl p-4 flex items-center gap-3 border border-white/10">
                <div className="flex-shrink-0 h-12 w-12 rounded-xl bg-white/15 flex items-center justify-center">
                    <Smartphone className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm" style={{ fontFamily: 'Chivo, sans-serif' }}>Install DIGI-LOANS</p>
                    {showIOSHint ? (
                        <p className="text-[11px] text-white/85 leading-tight mt-0.5">
                            Tap <span className="inline-block px-1 py-0.5 bg-white/15 rounded text-[10px] font-bold">Share ⎙</span> then <strong>Add to Home Screen</strong>
                        </p>
                    ) : (
                        <p className="text-[11px] text-white/85 leading-tight mt-0.5">Get the app on your phone — works offline, faster, with shortcuts.</p>
                    )}
                </div>
                {show && !showIOSHint && (
                    <button
                        onClick={handleInstall}
                        className="flex-shrink-0 bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold text-xs px-3 py-2 rounded-lg flex items-center gap-1 transition-all hover:shadow-lg"
                        data-testid="pwa-install-button"
                    >
                        <Download className="h-3.5 w-3.5" /> Install
                    </button>
                )}
                <button
                    onClick={dismiss}
                    className="flex-shrink-0 text-white/60 hover:text-white p-1 rounded-md transition-colors"
                    aria-label="Dismiss install prompt"
                    data-testid="pwa-install-dismiss"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
};

export default InstallPrompt;
